import React from "react";
import { toast } from "react-hot-toast";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, FormGroup, Label, Input } from "reactstrap";

/**
 * Modal para aplicar, modificar o eliminar descuentos manuales.
 * Componente puro: recibe estado y handlers como props.
 */
export function ModalDescuento({
  showDiscountModal,
  setShowDiscountModal,
  discountMethod,
  setDiscountMethod,
  finalAmountStr,
  setFinalAmountStr,
  manualDiscountAmountStr,
  setManualDiscountAmountStr,
  discountPercentageStr,
  setDiscountPercentageStr,
  finalAmount,
  setFinalAmount,
  manualDiscountAmount,
  setManualDiscountAmount,
  discountPercentage,
  setDiscountPercentage,
  setDiscountType,
  setOriginalAmount,
  setForceRecalc,
  selectedMoneda,
  inversionFinalTotal,
  impactosTotales,
  formatAmount,
  validateDiscount,
  isEditMode,
  rol,
  customModalClassName,
}) {
  const [targetCpmStr, setTargetCpmStr] = React.useState("");
  const cpmActual = impactosTotales > 0 ? (inversionFinalTotal / impactosTotales) * 1000 : 0;
  const cpmEsEntero = selectedMoneda === "$"; // Chile: CPM entero (5.000), Perú: decimal (23,0)

  return (
    <Modal isOpen={showDiscountModal} toggle={() => setShowDiscountModal(false)} className={customModalClassName}>
      <ModalHeader toggle={() => setShowDiscountModal(false)}>Aplicar Descuento Manual</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label className="mb-2">¿Qué descuento deseas realizar?</Label>
          <div className="d-flex flex-column gap-2">
            <FormGroup check>
              <Input
                type="radio"
                name="discountMethod"
                value="finalAmount"
                checked={discountMethod === "finalAmount"}
                onChange={() => {
                  setDiscountMethod("finalAmount");
                  setFinalAmountStr("");
                  setFinalAmount(0);
                }}
              />
              <Label check>Definir un monto objetivo</Label>
            </FormGroup>
            <FormGroup check>
              <Input
                type="radio"
                name="discountMethod"
                value="discountAmount"
                checked={discountMethod === "discountAmount"}
                onChange={() => {
                  setDiscountMethod("discountAmount");
                  setManualDiscountAmountStr("");
                  setManualDiscountAmount(0);
                }}
              />
              <Label check>Descontar un monto</Label>
            </FormGroup>
            <FormGroup check>
              <Input
                type="radio"
                name="discountMethod"
                value="percentage"
                checked={discountMethod === "percentage"}
                onChange={() => {
                  setDiscountMethod("percentage");
                  setDiscountPercentageStr("");
                  setDiscountPercentage(0);
                }}
              />
              <Label check>Descontar un porcentaje</Label>
            </FormGroup>
            <FormGroup check>
              <Input
                type="radio"
                name="discountMethod"
                value="cpm"
                checked={discountMethod === "cpm"}
                disabled={!impactosTotales}
                onChange={() => {
                  setDiscountMethod("cpm");
                  setTargetCpmStr("");
                }}
              />
              <Label check>Definir CPM objetivo</Label>
            </FormGroup>
          </div>
        </FormGroup>
        <FormGroup>
          <Label>
            {discountMethod === "finalAmount"
              ? "Ingresa el Monto Final Deseado"
              : discountMethod === "discountAmount"
                ? "Ingresa el Monto de Descuento"
                : discountMethod === "cpm"
                  ? "Ingresa el CPM Objetivo"
                  : "Ingresa el Porcentaje de Descuento"}
          </Label>
          {discountMethod === "cpm" && cpmActual > 0 && (
            <small className="text-muted d-block mb-2">
              CPM actual: {selectedMoneda}{" "}
              {cpmActual.toLocaleString(selectedMoneda === "$" ? "es-CL" : "es-PE", {
                minimumFractionDigits: selectedMoneda === "$" ? 0 : 1,
                maximumFractionDigits: selectedMoneda === "$" ? 0 : 1,
              })}
            </small>
          )}
          <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
            <Input
              type="text"
              value={
                discountMethod === "finalAmount"
                  ? finalAmountStr
                    ? formatAmount(parseInt(finalAmountStr, 10), selectedMoneda === "$" ? "CLP" : "PEN")
                    : ""
                  : discountMethod === "discountAmount"
                    ? manualDiscountAmountStr
                      ? formatAmount(parseInt(manualDiscountAmountStr, 10), selectedMoneda === "$" ? "CLP" : "PEN")
                      : ""
                    : discountMethod === "cpm"
                      ? cpmEsEntero
                        ? targetCpmStr
                          ? formatAmount(parseInt(targetCpmStr, 10), "CLP")
                          : ""
                        : targetCpmStr
                      : discountPercentageStr
              }
              onChange={(e) => {
                const usaDecimales = discountMethod === "percentage" || (discountMethod === "cpm" && !cpmEsEntero);
                let value = e.target.value.replace(/\D/g, "");
                if (usaDecimales) {
                  value = e.target.value.replace(/,/g, ".").replace(/[^\d.]/g, "");
                  const parts = value.split(".");
                  if (parts.length > 2) value = `${parts[0]}.${parts.slice(1).join("")}`;
                  if (discountMethod === "percentage") {
                    const numValue = parseFloat(value);
                    if (numValue > 100) value = "100";
                  }
                  if (parts[1]?.length > 2) value = `${parts[0]}.${parts[1].slice(0, 2)}`;
                }
                if (discountMethod === "finalAmount") setFinalAmountStr(value);
                else if (discountMethod === "discountAmount") setManualDiscountAmountStr(value);
                else if (discountMethod === "cpm") setTargetCpmStr(value);
                else setDiscountPercentageStr(value);
              }}
              onBlur={(e) => {
                const usaDecimales = discountMethod === "percentage" || (discountMethod === "cpm" && !cpmEsEntero);
                let rawValue = e.target.value.replace(/\D/g, "");
                if (usaDecimales) {
                  rawValue = e.target.value.replace(/\.$/, "");
                  const numValue = parseFloat(rawValue) || 0;
                  rawValue = discountMethod === "cpm" ? numValue.toFixed(1) : numValue.toFixed(2);
                } else {
                  rawValue = parseInt(rawValue, 10) || "";
                }
                if (discountMethod === "finalAmount") setFinalAmountStr(String(rawValue));
                else if (discountMethod === "discountAmount") setManualDiscountAmountStr(String(rawValue));
                else if (discountMethod === "cpm") setTargetCpmStr(String(rawValue));
                else setDiscountPercentageStr(String(rawValue));
              }}
              onFocus={(e) => e.target.select()}
              placeholder={
                discountMethod === "percentage"
                  ? "Ejemplo: 10.5 (máx 100)"
                  : discountMethod === "cpm"
                    ? cpmEsEntero
                      ? "Ejemplo: 5.000"
                      : "Ejemplo: 19.5"
                    : "Ejemplo: 3.500.000"
              }
              style={{
                paddingRight:
                  discountMethod === "percentage" || (discountMethod === "cpm" && !cpmEsEntero) ? "30px" : "8px",
              }}
              className="custom-input"
              invalid={discountMethod === "percentage" && parseFloat(discountPercentageStr) > 100}
            />
            {(discountMethod === "percentage" || (discountMethod === "cpm" && !cpmEsEntero)) && (
              <span
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#999",
                  fontSize: "1rem",
                  pointerEvents: "none",
                }}
              >
                {discountMethod === "percentage" ? "%" : selectedMoneda}
              </span>
            )}
            {discountMethod === "percentage" && parseFloat(discountPercentageStr) > 100 && (
              <div className="invalid-feedback">El porcentaje no puede exceder 100.</div>
            )}
          </div>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => setShowDiscountModal(false)}>
          Cancelar
        </Button>
        <Button
          color="primary"
          onClick={() => {
            let newFinalAmount = 0;
            let newDiscountAmount = 0;
            let newPercentage = 0;
            let newDiscountType = discountMethod; // 'finalAmount' -> 'final', etc.
            if (discountMethod === "finalAmount") {
              newFinalAmount = parseInt(finalAmountStr, 10) || 0;
              newDiscountAmount = inversionFinalTotal - newFinalAmount;
              newPercentage = inversionFinalTotal > 0 ? (newDiscountAmount / inversionFinalTotal) * 100 : 0;
              newDiscountType = "final";
            } else if (discountMethod === "discountAmount") {
              newDiscountAmount = parseInt(manualDiscountAmountStr, 10) || 0;
              newFinalAmount = inversionFinalTotal - newDiscountAmount;
              newPercentage = inversionFinalTotal > 0 ? (newDiscountAmount / inversionFinalTotal) * 100 : 0;
              newDiscountType = "amount";
            } else if (discountMethod === "percentage") {
              newPercentage = parseFloat(discountPercentageStr) || 0;
              if (rol === "comercial") newPercentage = Math.min(newPercentage, 10);
              else if (!["superadmin", "gerencia"].includes(rol)) newPercentage = 0;
              newDiscountAmount = (newPercentage / 100) * inversionFinalTotal;
              newFinalAmount = inversionFinalTotal - newDiscountAmount;
              newDiscountType = "percentage";
            } else if (discountMethod === "cpm") {
              const targetCpm = cpmEsEntero ? parseInt(targetCpmStr, 10) || 0 : parseFloat(targetCpmStr) || 0;
              if (impactosTotales > 0) {
                newFinalAmount = (targetCpm * impactosTotales) / 1000;
                newDiscountAmount = inversionFinalTotal - newFinalAmount;
                newPercentage = inversionFinalTotal > 0 ? (newDiscountAmount / inversionFinalTotal) * 100 : 0;
              }
              newDiscountType = "final";
            }
            newFinalAmount = Math.floor(newFinalAmount);
            newDiscountAmount = Math.floor(newDiscountAmount);
            newPercentage = parseFloat(newPercentage.toFixed(2));
            const error = validateDiscount(inversionFinalTotal, newDiscountAmount, newFinalAmount, rol);
            if (error) {
              toast.error(error);
              return;
            }
            setDiscountType(newDiscountType);
            setFinalAmount(newFinalAmount);
            setManualDiscountAmount(newDiscountAmount);
            setDiscountPercentage(newPercentage);
            setOriginalAmount(inversionFinalTotal);
            setFinalAmountStr(newFinalAmount.toString());
            setManualDiscountAmountStr(newDiscountAmount.toString());
            setDiscountPercentageStr(newPercentage.toString());
            setShowDiscountModal(false);
            setForceRecalc((prev) => prev + 1);
            toast("Descuento aplicado localmente. Guarda los cambios con 'Editar'.", {
              icon: "\u2139\uFE0F",
              duration: 5000,
            });
          }}
        >
          Confirmar
        </Button>
        {manualDiscountAmount > 0 && (
          <Button
            color="danger"
            onClick={() => {
              setDiscountType("none");
              setManualDiscountAmount(0);
              setFinalAmount(inversionFinalTotal);
              setDiscountPercentage(0);
              setOriginalAmount(inversionFinalTotal);
              setManualDiscountAmountStr("");
              setFinalAmountStr(inversionFinalTotal.toString());
              setDiscountPercentageStr("0");
              setShowDiscountModal(false);
              setForceRecalc((prev) => prev + 1);
              toast.success("Descuento eliminado");
            }}
          >
            Eliminar Descuento
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
